import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { DollarSign, UserX, Zap, Repeat } from 'lucide-react';
import { ACCENT, GREEN, BLUE, PURPLE, Spinner, KpiCard } from './shared';

const TOP_PERIODS = [
  { v: 'all',     l: 'Sempre' },
  { v: 'month',   l: 'Mese' },
  { v: 'quarter', l: 'Trim.' },
  { v: 'year',    l: 'Anno' },
];

export function CustomersTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topView, setTopView] = useState('spend');
  const [topPeriod, setTopPeriod] = useState('all');
  useEffect(() => {
    setLoading(true);
    adminApi
      .get(`/admin/analytics/business?period=month&top_period=${topPeriod}`)
      .then(r => setD(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [topPeriod]);
  if (loading && !d) return <Spinner />;
  const top = d?.top_customers || {};
  const ret = d?.retention || {};
  const kpi = d?.kpi || {};
  const topData = topView === 'spend' ? top.by_spend : topView === 'visits' ? top.by_visits : top.by_points;

  return (
    <div className="space-y-6" data-testid="customers-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="LTV medio" value={`\u20AC${kpi.ltv}`} icon={DollarSign} color={BLUE} testid="cust-ltv" />
        <KpiCard label="Churn rate" value={`${kpi.churn_rate}%`} icon={UserX} color="#EF4444" testid="cust-churn" />
        <KpiCard label="Clienti attivi" value={kpi.active_30d} icon={Zap} color={GREEN} sub="Ultimi 30 giorni" testid="cust-active" />
        <KpiCard label="Freq. media" value={`${d?.averages?.avg_frequency || 0}x`} icon={Repeat} color={PURPLE} sub="Visite/mese per cliente" testid="cust-freq" />
      </div>
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-2 gap-2">
          <h3 className="font-semibold text-[#1A1A1A]">Top 10 clienti</h3>
          <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
            {[{v:'spend',l:'Spesa'},{v:'visits',l:'Visite'},{v:'points',l:'Punti'}].map(p => (
              <button key={p.v} onClick={() => setTopView(p.v)} data-testid={`top-${p.v}`}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${topView === p.v ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B]'}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Periodo</span>
          <div className="flex gap-1 bg-slate-50 p-0.5 rounded-lg" data-testid="top-period-switch">
            {TOP_PERIODS.map(p => (
              <button key={p.v} onClick={() => setTopPeriod(p.v)} data-testid={`top-period-${p.v}`}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${topPeriod === p.v ? 'bg-white text-[#D44E20] shadow-sm border border-slate-200' : 'text-[#64748B]'}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {(topData || []).length === 0 && (
            <p className="text-sm text-[#94A3B8] text-center py-6" data-testid="top-empty">
              Nessun acquisto nel periodo selezionato.
            </p>
          )}
          {(topData || []).map((c, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`top-customer-${i}`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ background: i < 3 ? [ACCENT, '#C0C0C0', '#CD7F32'][i] : '#ddd', color: i < 3 ? 'white' : '#888' }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{c.name}</p>
                <p className="text-[10px] text-[#64748B]">{c.phone} &middot; {c.tier}</p>
              </div>
              <span className="font-bold text-sm tabular-nums" style={{ color: ACCENT }}>
                {topView === 'spend' ? `\u20AC${c.total_spent?.toLocaleString('it-IT')}` :
                 topView === 'visits' ? `${c.visits} visite` : `${c.lifetime_points?.toLocaleString('it-IT')} pt`}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <h3 className="font-semibold text-[#1A1A1A] mb-4">Tasso di ritorno</h3>
        <p className="text-[10px] text-[#64748B] mb-4">Clienti con 2+ visite che sono tornati negli ultimi N giorni (su {ret.returning_base} clienti con almeno 1 visita)</p>
        <div className="space-y-4">
          {[
            { label: 'Ultimi 30 giorni', value: ret.ret_30d, rate: ret.rate_30d, color: GREEN },
            { label: 'Ultimi 60 giorni', value: ret.ret_60d, rate: ret.rate_60d, color: BLUE },
            { label: 'Ultimi 90 giorni', value: ret.ret_90d, rate: ret.rate_90d, color: PURPLE },
          ].map(item => (
            <div key={item.label}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#64748B]">{item.label}</span>
                <span className="font-bold" style={{ color: item.color }}>{item.value} ({item.rate}%)</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${item.rate}%`, background: item.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
